import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import * as storage from '../services/storage';

// Workspace Context - tracks currently selected workspace
const WorkspaceContext = createContext(null);

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function WorkspaceProvider({ children }) {
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch workspaces
  const fetchWorkspaces = useCallback(async () => {
    try {
      setLoading(true);
      
      // Initialize DB and ensure main workspace exists
      await storage.initDB();
      const mainWs = await storage.getMainWorkspace();
      
      // Now fetch workspaces list (main workspace is guaranteed to exist)
      const wsList = await storage.getWorkspaces();
      
      setWorkspaces(wsList);
      
      // Load saved workspace or default to main
      const savedWsId = localStorage.getItem('current_workspace_id');
      const savedWs = wsList.find(w => w.id === savedWsId);
      
      if (savedWs) {
        setCurrentWorkspace(savedWs);
      } else {
        setCurrentWorkspace(mainWs);
        localStorage.setItem('current_workspace_id', mainWs.id);
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const selectWorkspace = useCallback((workspace) => {
    setCurrentWorkspace(workspace);
    localStorage.setItem('current_workspace_id', workspace.id);
  }, []);

  const value = {
    currentWorkspace,
    workspaces,
    loading,
    selectWorkspace,
    refetch: fetchWorkspaces
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

// Cards hooks - workspace-scoped
export function useCards(filters = {}) {
  const { currentWorkspace } = useWorkspace();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Extract filter values to avoid object reference issues
  const filterTag = filters.tag;
  const filterDeck = filters.deck;
  const filterSearch = filters.search;

  useEffect(() => {
    let isMounted = true;

    async function fetchCards() {
      if (!currentWorkspace) {
        setCards([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await storage.getCards(currentWorkspace.id, {
          tag: filterTag,
          deck: filterDeck,
          search: filterSearch
        });
        if (isMounted) {
          setCards(result);
        }
      } catch (err) {
        console.error('Error fetching cards:', err);
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchCards();

    return () => {
      isMounted = false;
    };
  }, [currentWorkspace, filterTag, filterDeck, filterSearch, refreshKey]);

  // Simple refetch function that triggers a re-fetch
  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return { cards, loading, error, refetch };
}

export function useCard(cardId) {
  const { currentWorkspace } = useWorkspace();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCard = useCallback(async () => {
    if (!cardId || !currentWorkspace) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await storage.getCard(currentWorkspace.id, cardId);
      setCard(result);
    } catch (err) {
      console.error('Failed to fetch card:', err);
    } finally {
      setLoading(false);
    }
  }, [cardId, currentWorkspace]);

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  return { card, loading, refetch: fetchCard };
}

// Decks hooks - workspace-scoped
export function useDecks() {
  const { currentWorkspace } = useWorkspace();
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDecks = useCallback(async () => {
    if (!currentWorkspace) {
      setDecks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await storage.getDecks(currentWorkspace.id);
      setDecks(result);
    } catch (err) {
      console.error('Failed to fetch decks:', err);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    fetchDecks();
  }, [fetchDecks]);

  return { decks, loading, refetch: fetchDecks };
}

// Stats hook - workspace-scoped
export function useStats() {
  const { currentWorkspace } = useWorkspace();
  const [stats, setStats] = useState({ total_cards: 0, total_decks: 0 });

  useEffect(() => {
    async function fetchStats() {
      if (!currentWorkspace) return;

      try {
        const result = await storage.getStats(currentWorkspace.id);
        setStats(result);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    }

    fetchStats();
  }, [currentWorkspace]);

  return stats;
}

// Search hook - workspace-scoped
export function useSearch() {
  const { currentWorkspace } = useWorkspace();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (q) => {
    if (!currentWorkspace || !q.trim()) {
      setResults([]);
      return;
    }

    try {
      setLoading(true);
      const result = await storage.searchCards(currentWorkspace.id, q);
      setResults(result);
    } catch (err) {
      console.error('Search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace]);

  const clear = useCallback(() => {
    setResults([]);
  }, []);

  return { results, loading, search, clear };
}

// Undo hook
export function useUndo() {
  const [lastDeleted, setLastDeleted] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkUndo = useCallback(async () => {
    try {
      const deleted = await storage.getLastDeletedItem();
      if (deleted) {
        setLastDeleted({
          has_undo: true,
          item_type: deleted.item_type,
          item_title: deleted.item_data?.title || deleted.item_data?.name || 'Unknown',
          deleted_at: deleted.deleted_at,
          cascade_count: deleted.cascade_data?.length || 0
        });
      } else {
        setLastDeleted(null);
      }
    } catch (err) {
      console.error('Failed to check undo:', err);
    }
  }, []);

  const performUndo = useCallback(async () => {
    try {
      setLoading(true);
      const result = await storage.undoLastDelete();
      setLastDeleted(null);
      return result;
    } catch (err) {
      console.error('Undo failed:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUndo();
  }, [checkUndo]);

  return { lastDeleted, loading, checkUndo, performUndo };
}

// Storage API - for direct operations
export function useStorage() {
  const { currentWorkspace } = useWorkspace();

  return {
    // Card operations
    createCard: (data) => storage.createCard(currentWorkspace?.id, data),
    updateCard: (cardId, updates) => storage.updateCard(currentWorkspace?.id, cardId, updates),
    deleteCard: (cardId, cascade = true) => storage.deleteCard(currentWorkspace?.id, cardId, cascade),
    getCardDeletionPreview: (cardId) => storage.getCardDeletionPreview(currentWorkspace?.id, cardId),
    getCardReferences: (cardId) => storage.getCardReferences(currentWorkspace?.id, cardId),
    searchCards: (query) => storage.searchCards(currentWorkspace?.id, query),
    saveCard: storage.saveCard, // Direct save without bidirectional linking
    cleanupOrphanedCards: () => storage.cleanupOrphanedCards(currentWorkspace?.id),
    
    // Deck operations
    createDeck: (name, description, seedCardIds) => storage.createDeck(currentWorkspace?.id, name, description, seedCardIds),
    addCardsToDeck: (deckId, cardIds) => storage.addCardsToDeck(currentWorkspace?.id, deckId, cardIds),
    deleteDeck: (deckId) => storage.deleteDeck(currentWorkspace?.id, deckId),
    exportDeck: (deckId) => storage.exportDeck(currentWorkspace?.id, deckId),
    saveDeck: storage.saveDeck,
    
    // Import
    previewImport: (deckName, cards) => storage.previewImport(currentWorkspace?.id, deckName, cards),
    confirmImport: (previewId) => storage.confirmImport(currentWorkspace?.id, previewId),
    
    // Workspace operations
    createWorkspace: storage.createWorkspace,
    updateWorkspace: storage.updateWorkspace,
    deleteWorkspace: storage.deleteWorkspace,
    duplicateWorkspace: storage.duplicateWorkspace,
    saveWorkspace: storage.saveWorkspace,
    
    // Backup
    exportAllData: storage.exportAllData,
    importAllData: storage.importAllData,
    clearAllData: storage.clearAllData
  };
}

export default WorkspaceProvider;
